<template>
  <v-container fluid class="review-form-container">
    <v-row justify="center">
      <v-col cols="12" sm="12" md="12" lg="12" xl="12">
        <v-card class="review-card elevation-8" :loading="pageLoading">
          <!-- Header Section -->
          <v-card-title class="review-header pa-0">
            <v-toolbar flat color="primary" dark class="review-toolbar">
              <v-toolbar-title class="font-weight-600 font-size-20">
                Leave a Review
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
          </v-card-title>

          <!-- Show form only if token is valid -->
          <div v-if="tokenValidated && !tokenError">
            <v-card-text class="pa-6">
              <v-form ref="reviewForm" v-model="formValid" lazy-validation>
                <!-- Service/Company Info -->
                <div class="service-info mb-6">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="48" class="mr-3" color="#252323">
                      <v-icon color="#fff" size="24">mdi-home-search</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="font-weight-600 font-size-18 mb-1">
                        {{ serviceName }}
                      </h3>
                      <p class="text--secondary font-size-14 mb-0">
                        Share your experience with others
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Desktop Layout for Web -->
                <div class="desktop-layout d-none d-md-block">
                  <v-row>
                    <!-- Left Column -->
                    <v-col cols="6">
                      <!-- Rating Section -->
                      <div class="rating-section mb-6">
                        <h4
                          class="font-weight-600 font-size-16 mb-3 color-primary"
                        >
                          Rating
                        </h4>
                        <div class="text-center mb-2">
                          <v-rating
                            v-model="reviewData.rating"
                            color="amber"
                            background-color="grey lighten-1"
                            size="45"
                            hover
                            :rules="ratingRules"
                            class="rating-stars"
                          ></v-rating>
                        </div>
                      </div>

                      <!-- Comments Section -->
                      <div class="comments-section mb-6">
                        <h4
                          class="font-weight-600 font-size-16 mb-3 color-primary"
                        >
                          Comments
                        </h4>
                        <div class="textarea-wrappe">
                          <v-textarea
                            v-model="reviewData.comments"
                            rows="3"
                            placeholder="Write your comments here..."
                            auto-grow
                            counter="500"
                            maxlength="500"
                            outlined
                            class="custom-textarea-fixe"
                            :rules="commentRules"
                            hide-details
                            no-resize
                          >
                          </v-textarea>
                          <div
                            class="text-caption grey--text d-flex justify-content-end"
                          >
                            {{ reviewData.comments.length }}/500
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Right Column -->
                    <v-col cols="6">
                      <!-- Service Questions -->
                      <div class="service-questions mb-6">
                        <!-- Punctual and Professional -->
                        <div class="question-item mb-4">
                          <h4 class="font-weight-500 font-size-16 mb-3">
                            Was the cleaner punctual and professional?
                          </h4>
                          <p class="font-size-12 text--secondary mb-3">
                            Your opinion won't be posted publicly
                          </p>
                          <div class="thumb-buttons-container">
                            <v-btn
                              @click="reviewData.punctual_professional = 'no'"
                              :class="[
                                'thumb-btn',
                                'thumb-no',
                                {
                                  active:
                                    reviewData.punctual_professional === 'no',
                                },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.punctual_professional === 'no'
                                    ? 'red'
                                    : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-down
                              </v-icon>
                            </v-btn>
                            <v-btn
                              @click="reviewData.punctual_professional = 'yes'"
                              :class="[
                                'thumb-btn',
                                'thumb-yes',
                                {
                                  active:
                                    reviewData.punctual_professional === 'yes',
                                },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.punctual_professional === 'yes'
                                    ? 'green'
                                    : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-up
                              </v-icon>
                            </v-btn>
                          </div>
                        </div>

                        <!-- Cleaning Satisfaction -->
                        <div class="question-item mb-4">
                          <h4 class="font-weight-500 font-size-16 mb-3">
                            Was the cleaning completed to your
                            satisfaction?
                          </h4>
                          <div class="thumb-buttons-container">
                            <v-btn
                              @click="reviewData.cleaning_satisfaction = 'no'"
                              :class="[
                                'thumb-btn',
                                'thumb-no',
                                {
                                  active:
                                    reviewData.cleaning_satisfaction === 'no',
                                },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.cleaning_satisfaction === 'no'
                                    ? 'red'
                                    : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-down
                              </v-icon>
                            </v-btn>
                            <v-btn
                              @click="reviewData.cleaning_satisfaction = 'yes'"
                              :class="[
                                'thumb-btn',
                                'thumb-yes',
                                {
                                  active:
                                    reviewData.cleaning_satisfaction === 'yes',
                                },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.cleaning_satisfaction === 'yes'
                                    ? 'green'
                                    : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-up
                              </v-icon>
                            </v-btn>
                          </div>
                        </div>

                        <!-- Recommendation -->
                        <div class="question-item mb-4">
                          <h4 class="font-weight-500 font-size-16 mb-3">
                            Would you recommend {{ serviceName }} to others?
                          </h4>
                          <div class="thumb-buttons-container">
                            <v-btn
                              @click="reviewData.recommend = 'no'"
                              :class="[
                                'thumb-btn',
                                'thumb-no',
                                { active: reviewData.recommend === 'no' },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.recommend === 'no' ? 'red' : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-down
                              </v-icon>
                            </v-btn>
                            <v-btn
                              @click="reviewData.recommend = 'yes'"
                              :class="[
                                'thumb-btn',
                                'thumb-yes',
                                { active: reviewData.recommend === 'yes' },
                              ]"
                              outlined
                              large
                            >
                              <v-icon
                                :color="
                                  reviewData.recommend === 'yes'
                                    ? 'green'
                                    : 'grey'
                                "
                                size="24"
                              >
                                mdi-thumb-up
                              </v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>

                      <!-- Additional Feedback -->
                      <div class="additional-feedback mb-6">
                        <v-expansion-panels flat>
                          <v-expansion-panel class="custom-expansion-panel">
                            <v-expansion-panel-header class="px-0 py-2">
                              <div class="d-flex align-center">
                                <v-icon class="mr-2" color="primary"
                                  >mdi-comment-plus</v-icon
                                >
                                <span class="font-weight-500"
                                  >Additional Feedback (Optional)</span
                                >
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="px-0">
                              <div class="textarea-wrapper">
                                <v-textarea
                                  v-model="reviewData.additional_feedback"
                                  placeholder="Any additional comments or suggestions?"
                                  rows="3"
                                  outlined
                                  counter="300"
                                  maxlength="300"
                                  class="mt-3 additional-textarea-fixed"
                                  hide-details="auto"
                                  no-resize
                                ></v-textarea>
                                <div
                                  class="text-caption grey--text d-flex justify-content-end"
                                >
                                  {{
                                    reviewData.additional_feedback.length
                                  }}/300
                                </div>
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Mobile Layout -->
                <div class="mobile-layout d-block d-md-none">
                  <!-- Rating Section -->
                  <div class="rating-section mb-6">
                    <h4 class="font-weight-600 font-size-16 mb-3 color-primary">
                      Rating
                    </h4>
                    <div class="text-center mb-2">
                      <v-rating
                        v-model="reviewData.rating"
                        color="amber"
                        background-color="grey lighten-1"
                        size="45"
                        hover
                        :rules="ratingRules"
                        class="rating-stars"
                      ></v-rating>
                    </div>
                  </div>

                  <!-- Comments Section -->
                  <div class="comments-section mb-6">
                    <h4 class="font-weight-600 font-size-16 mb-3 color-primary">
                      Comments
                    </h4>
                    <div class="textarea-wrappe">
                      <v-textarea
                        v-model="reviewData.comments"
                        placeholder="Share your experience... (Optional)"
                        outlined
                        rows="1"
                        auto-grow
                        counter="500"
                        maxlength="500"
                        class="custom-textarea-fixe rounded-xl"
                        :rules="commentRules"
                        hide-details="auto"
                        no-resize
                      ></v-textarea>
                      <div
                        class="text-caption grey--text d-flex justify-content-end"
                      >
                        {{ reviewData.comments.length }}/500
                      </div>
                    </div>
                  </div>

                  <!-- Service Questions -->
                  <div class="service-questions mb-6">
                    <!-- Punctual and Professional -->
                    <div class="question-item mb-4">
                      <h4 class="font-weight-500 font-size-16 mb-3">
                        Was the cleaner punctual and professional?
                      </h4>
                      <p class="font-size-12 text--secondary mb-3">
                        Your opinion won't be posted publicly
                      </p>
                      <div class="thumb-buttons-container">
                        <v-btn
                          @click="reviewData.punctual_professional = 'no'"
                          :class="[
                            'thumb-btn',
                            'thumb-no',
                            {
                              active: reviewData.punctual_professional === 'no',
                            },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.punctual_professional === 'no'
                                ? 'red'
                                : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-down
                          </v-icon>
                        </v-btn>
                        <v-btn
                          @click="reviewData.punctual_professional = 'yes'"
                          :class="[
                            'thumb-btn',
                            'thumb-yes',
                            {
                              active:
                                reviewData.punctual_professional === 'yes',
                            },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.punctual_professional === 'yes'
                                ? 'green'
                                : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-up
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Cleaning Satisfaction -->
                    <div class="question-item mb-4">
                      <h4 class="font-weight-500 font-size-16 mb-3">
                        Was the cleaning completed to your satisfaction?
                      </h4>
                      <div class="thumb-buttons-container">
                        <v-btn
                          @click="reviewData.cleaning_satisfaction = 'no'"
                          :class="[
                            'thumb-btn',
                            'thumb-no',
                            {
                              active: reviewData.cleaning_satisfaction === 'no',
                            },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.cleaning_satisfaction === 'no'
                                ? 'red'
                                : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-down
                          </v-icon>
                        </v-btn>
                        <v-btn
                          @click="reviewData.cleaning_satisfaction = 'yes'"
                          :class="[
                            'thumb-btn',
                            'thumb-yes',
                            {
                              active:
                                reviewData.cleaning_satisfaction === 'yes',
                            },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.cleaning_satisfaction === 'yes'
                                ? 'green'
                                : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-up
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Recommendation -->
                    <div class="question-item mb-4">
                      <h4 class="font-weight-500 font-size-16 mb-3">
                        Would you recommend {{ serviceName }} to others?
                      </h4>
                      <div class="thumb-buttons-container">
                        <v-btn
                          @click="reviewData.recommend = 'no'"
                          :class="[
                            'thumb-btn',
                            'thumb-no',
                            { active: reviewData.recommend === 'no' },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.recommend === 'no' ? 'red' : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-down
                          </v-icon>
                        </v-btn>
                        <v-btn
                          @click="reviewData.recommend = 'yes'"
                          :class="[
                            'thumb-btn',
                            'thumb-yes',
                            { active: reviewData.recommend === 'yes' },
                          ]"
                          outlined
                          large
                        >
                          <v-icon
                            :color="
                              reviewData.recommend === 'yes' ? 'green' : 'grey'
                            "
                            size="24"
                          >
                            mdi-thumb-up
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <!-- Additional Feedback -->
                  <div class="additional-feedback mb-6">
                    <v-expansion-panels flat>
                      <v-expansion-panel class="custom-expansion-panel">
                        <v-expansion-panel-header class="px-0 py-2">
                          <div class="d-flex align-center">
                            <v-icon class="mr-2" color="primary"
                              >mdi-comment-plus</v-icon
                            >
                            <span class="font-weight-500"
                              >Additional Feedback (Optional)</span
                            >
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="px-0">
                          <div class="textarea-wrapper">
                            <v-textarea
                              v-model="reviewData.additional_feedback"
                              placeholder="Any additional comments or suggestions?"
                              outlined
                              rows="1"
                              counter="300"
                              maxlength="300"
                              class="mt-3 additional-textarea-fixed"
                              hide-details="auto"
                              no-resize
                            ></v-textarea>
                            <div
                              class="text-caption grey--text d-flex justify-content-end"
                            >
                              {{ reviewData.additional_feedback.length }}/300
                            </div>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                </div>
              </v-form>
            </v-card-text>

            <!-- Action Buttons -->
            <v-card-actions class="pa-6 pt-0 justify-content-center">
              <!-- <v-spacer></v-spacer> -->
              <v-btn
                outlined
                color="grey"
                class="mr-3 rounded-xl"
                :disabled="submitting"
              >
                Cancel
              </v-btn>
              <v-btn
                depressed
                color="#4caf50"
                class="submit-btn font-weight-600 mt-0"
                :loading="submitting"
                :disabled="!isFormValid"
                @click="submitReview"
                elevation="2"
              >
                <v-icon class="mr-2">mdi-send</v-icon>
                Submit Review
              </v-btn>
            </v-card-actions>
          </div>

          <!-- Loading state while validating token -->
          <div
            v-else-if="!tokenValidated && !tokenError"
            class="text-center pa-6"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="mt-4 text--secondary">Validating your review link...</p>
          </div>

          <!-- Error state for invalid token -->
          <div v-else-if="tokenError" class="text-center pa-6">
            <v-icon size="64" color="error" class="mb-4"
              >mdi-alert-circle</v-icon
            >
            <h3 class="font-weight-600 mb-2 error--text">Invalid Link</h3>
            <p class="text--secondary text-body-1 mb-4">
              This review link is invalid or has expired. Please use the
              original link from your email.
            </p>
            <v-btn color="cyan" @click="refreshPage">
              <v-icon left>mdi-refresh</v-icon>
              Refresh Page
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Response Dialog -->
    <v-dialog
      v-model="responseDialog"
      persistent
      max-width="500"
      :attach="dialogAttach"
    >
      <v-card class="text-center pa-4">
        <v-card-text>
          <div v-if="dialogState.type === 'success'">
            <v-icon size="64" color="#4caf50" class="mb-4">
              mdi-check-circle
            </v-icon>
            <h3 class="font-weight-600 mb-2 success--text">Success!</h3>
            <p class="text--secondary text-body-1">
              {{ dialogState.message }}
            </p>
          </div>

          <!-- Error State -->
          <div v-else-if="dialogState.type === 'error'">
            <v-icon size="64" color="#f44336" class="mb-4">
              mdi-alert-circle
            </v-icon>
            <h3 class="font-weight-600 mb-2 error--text">Oops!</h3>
            <p class="text--secondary text-body-1">
              {{ dialogState.message }}
            </p>
            <!-- Show additional error details if available -->
            <v-expansion-panels v-if="dialogState.details" flat class="mt-3">
              <v-expansion-panel>
                <v-expansion-panel-header class="pa-2">
                  <span class="text-caption">Error Details</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-2">
                  <div class="text-caption text-left error-details">
                    {{ dialogState.details }}
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <!-- Warning State -->
          <div v-else-if="dialogState.type === 'warning'">
            <v-icon size="64" color="#f44336" class="mb-4"> mdi-alert </v-icon>
            <h3 class="font-weight-600 mb-2 warning--text">Notice</h3>
            <p class="text--secondary text-body-1">
              {{ dialogState.message }}
            </p>
          </div>
        </v-card-text>

        <v-card-actions class="justify-center">
          <!-- Success Actions -->
          <template v-if="dialogState.type === 'success'">
            <div class="text-center w-100">
              <v-btn
                depressed
                color="#4caf50"
                @click="handleSuccessClose"
                class="px-8 rounded-xl"
              >
                <v-icon left>mdi-check</v-icon>
                Done
              </v-btn>
            </div>
          </template>

          <!-- Error Actions -->
          <template v-else-if="dialogState.type === 'error'">
            <div class="text-center w-100">
              <v-btn
                outlined
                color="grey"
                @click="closeDialog"
                class="mr-3 rounded-xl"
              >
                Cancel
              </v-btn>
              <v-btn
                class="rounded-xl"
                depressed
                color="cyan"
                @click="retrySubmission"
                :loading="submitting"
              >
                <v-icon left>mdi-refresh</v-icon>
                Try Again
              </v-btn>
            </div>
          </template>

          <!-- Warning Actions -->
          <template v-else-if="dialogState.type === 'warning'">
            <v-btn
              color="cyan"
              @click="closeDialog"
              class="mx-2 custom-grey-border custom-bold-button white--text"
            >
              <v-icon left>mdi-check</v-icon>
              Okay
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Token Invalid Dialog -->
    <v-dialog
      v-model="tokenInvalidDialog"
      persistent
      max-width="400"
      :attach="dialogAttach"
    >
      <v-card class="text-center pa-4">
        <v-card-text>
          <v-icon size="64" color="#f44336" class="mb-4"
            >mdi-shield-alert</v-icon
          >
          <h3 class="font-weight-600 mb-2 error--text">Token Invalid</h3>
          <p class="text--secondary text-body-1">
            The review link has been tampered with or is invalid. The page will
            refresh to restore the original link.
          </p>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            depressed
            color="#4caf50"
            @click="handleTokenInvalidOk"
            class="px-8"
          >
            <v-icon left>mdi-check</v-icon>
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { mapActions } from "vuex";
import ApiService from "@/core/services/api.service";

import { VERIFY_QUOTATION_CUSTOMER } from "@/core/services/store/request.module";

export default {
  name: "CreateReview",

  props: {
    serviceName: {
      type: String,
      default: "Snappy Home",
    },
  },

  data() {
    return {
      pageLoading: false,
      submitting: false,
      lastSubmittedRating: 0,
      formValid: false,
      responseDialog: false,
      tokenInvalidDialog: false,
      dialogAttach: null,
      dialogState: {
        type: null,
        message: "",
        details: null,
      },
      reviewData: {
        rating: 0,
        comments: "",
        punctual_professional: null,
        cleaning_satisfaction: null,
        recommend: null,
        additional_feedback: "",
      },
      token: null,
      originalToken: null,
      tokenValidated: false,
      tokenError: false,
      ratingRules: [(v) => v > 0 || "Please provide a rating"],
      commentRules: [
        (v) =>
          !v || v.length <= 500 || "Comments must be less than 500 characters",
      ],
    };
  },

  computed: {
    isFormValid() {
      return (
        this.reviewData.rating > 0 &&
        this.reviewData.punctual_professional !== null &&
        this.reviewData.cleaning_satisfaction !== null &&
        this.reviewData.recommend !== null
      );
    },
  },

  methods: {
    ...mapActions({
      verifyQuotationCustomer: VERIFY_QUOTATION_CUSTOMER,
    }),

    showDialog(type, message, details = null) {
      this.dialogState = {
        type,
        message,
        details,
      };
      this.responseDialog = true;
    },

    closeDialog() {
      this.responseDialog = false;
      this.dialogState = {
        type: null,
        message: "",
        details: null,
      };
    },
    handleSuccessClose() {
      if (this.lastSubmittedRating >= 4) {
        ApiService.get("reviews/get-google-review-link")
          .then((res) => {
            if (res.data?.url) {
              window.open(res.data.url, "_blank");
            }
            this.responseDialog = false;
          })
          .catch(() => {
            this.responseDialog = false;
          });
      } else {
        this.responseDialog = false;
      }
    },

    async retrySubmission() {
      this.closeDialog();
      await this.submitReview();
    },

    refreshPage() {
      window.location.reload();
    },

    handleTokenInvalidOk() {
      this.tokenInvalidDialog = false;
      window.location.reload();
    },

    // New method to validate JWT token
    async validateToken() {
      if (!this.token) {
        this.tokenError = true;
        return false;
      }

      try {
        // Decode JWT payload to check basic structure and expiration
        const tokenParts = this.token.split(".");
        if (tokenParts.length !== 3) {
          throw new Error("Invalid token format");
        }

        // Decode payload
        const payload = JSON.parse(atob(tokenParts[1]));

        // Check if token is expired
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < currentTime) {
          throw new Error("Token has expired");
        }

        // Validate token with backend using the new method
        const response = await ApiService.getWithCustomToken(
          `reviews/validate-token?token=${this.token}`,
          this.token
        );

        if (response.data && response.data.valid === true) {
          this.tokenValidated = true;
          return true;
        } else {
          throw new Error("Token validation failed on server");
        }
      } catch (error) {
        console.error("Token validation error:", error);
        this.tokenError = true;
        this.tokenValidated = false;
        return false;
      }
    },

    // Method to check if token has been tampered with
    checkTokenIntegrity() {
      const currentToken = this.$route.query.token;

      if (this.originalToken && currentToken !== this.originalToken) {
        // Token has been changed in URL
        this.tokenInvalidDialog = true;
        return false;
      }

      return true;
    },

    async submitReview() {
      // Check token integrity before submission
      if (!this.checkTokenIntegrity()) {
        return;
      }

      if (!this.$refs.reviewForm.validate()) {
        this.showDialog(
          "warning",
          "Please fill in all required fields correctly."
        );
        return;
      }

      if (!this.token) {
        this.showDialog(
          "error",
          "Authentication token is missing. Please refresh the page and try again."
        );
        return;
      }

      this.submitting = true;

      try {
        const reviewPayload = {
          rating: this.reviewData.rating,
          punctual_professional: this.reviewData.punctual_professional,
          cleaning_satisfaction: this.reviewData.cleaning_satisfaction,
          recommend: this.reviewData.recommend,
          comments: this.reviewData.comments || null,
          additional_feedback: this.reviewData.additional_feedback || null,
        };

        console.log("Submitting review:", reviewPayload);

        const response = await this.verifyQuotationCustomer({
          url: "reviews",
          data: reviewPayload,
          token: this.token,
        });

        console.log("Review response:", response);

        if (response && response.success === true) {
          this.lastSubmittedRating = this.reviewData.rating;
          this.showDialog(
            "success",
            response.message ||
              "Your review has been submitted successfully! Thank you for your feedback."
          );
          this.resetForm();
          this.$toast?.success("Review submitted successfully!");
        } else {
          throw new Error(response?.message || "Failed to submit review");
        }
      } catch (error) {
        console.error("Error submitting review:", error);

        let errorMessage = "Failed to submit review. Please try again.";
        let errorDetails = null;

        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 401:
              // Token is invalid or expired
              this.tokenInvalidDialog = true;
              this.submitting = false;
              return;
            case 422:
              errorMessage =
                data.message ||
                "You have already submitted a review for this service.";
              if (data.errors) {
                errorDetails = JSON.stringify(data.errors, null, 2);
              }
              break;
            case 500:
              errorMessage =
                data.message ||
                "Server error occurred. Please contact support if this continues.";
              if (data.debug) {
                errorDetails = JSON.stringify(data.debug, null, 2);
              }
              break;
            case 404:
              errorMessage =
                "Review service not found. Please contact support.";
              break;
            default:
              errorMessage =
                data.message || `Error ${status}: ${error.response.statusText}`;
              if (data.debug || data.errors) {
                errorDetails = JSON.stringify(
                  data.debug || data.errors,
                  null,
                  2
                );
              }
          }
        } else if (error.request) {
          errorMessage =
            "Network error. Please check your internet connection and try again.";
          errorDetails = "No response received from server";
        } else {
          errorMessage = error.message || "An unexpected error occurred.";
          errorDetails = error.stack || error.toString();
        }

        this.showDialog("error", errorMessage, errorDetails);
        this.$toast?.error(errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.reviewData = {
        rating: 0,
        comments: "",
        punctual_professional: null,
        cleaning_satisfaction: null,
        recommend: null,
        additional_feedback: "",
      };
      if (this.$refs.reviewForm) {
        this.$refs.reviewForm.resetValidation();
      }
    },

    ensureAppContainer() {
      let appElement = document.querySelector("[data-app]");
      if (!appElement) {
        appElement = document.createElement("div");
        appElement.setAttribute("data-app", "true");
        appElement.style.position = "relative";
        document.body.appendChild(appElement);
        console.log("Created [data-app] container");
      }
      return appElement;
    },
  },

  async mounted() {
    this.token = this.$route.query.token;
    this.originalToken = this.token;

    console.log(
      "Review form mounted with token:",
      this.token ? "Present" : "Missing"
    );

    this.dialogAttach = this.ensureAppContainer();

    // Validate token on mount
    if (this.token) {
      this.pageLoading = true;
      const isValid = await this.validateToken();
      this.pageLoading = false;

      if (!isValid) {
        console.log("Token validation failed");
      }
    } else {
      this.tokenError = true;
    }
  },

  watch: {
    $route(to) {
      const newToken = to.query.token;
      if (this.originalToken && newToken !== this.originalToken) {
        this.tokenInvalidDialog = true;
      }
    },
  },
};
</script>

<style scoped>
.review-form-container {
  min-height: 100vh;
  padding: 10px !important;
}

.review-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: white;
  max-width: 100%;
}

.review-toolbar {
  border-radius: 0 !important;
}

.color-primary {
  color: #1976d2 !important;
}

.font-size-12 {
  font-size: 12px !important;
}
.font-size-14 {
  font-size: 14px !important;
}
.font-size-16 {
  font-size: 16px !important;
}
.font-size-18 {
  font-size: 18px !important;
}
.font-size-20 {
  font-size: 20px !important;
}

.font-weight-500 {
  font-weight: 500 !important;
}
.font-weight-600 {
  font-weight: 600 !important;
}

.service-info {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
}

.rating-section {
  text-align: center;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  border: 2px dashed #e0e0e0;
}

/* Rating stars styling */
.rating-stars ::v-deep .v-icon {
  margin: 0 4px;
  color: #ffc107 !important;
}

.rating-stars ::v-deep .v-rating .v-icon--link {
  transition: all 0.2s ease;
}

.rating-stars ::v-deep .v-rating .v-icon--link:hover {
  color: #ff8f00 !important;
  transform: scale(1.1);
}

/* TEXTAREA FIX */
.textarea-wrapper {
  position: relative;
  /* overflow: hidden; */
  border-radius: 12px;
}

.custom-textarea-fixed {
  position: relative;
}

.custom-textarea-fixed ::v-deep .v-input__control {
  border-radius: 12px;
  /* overflow: hidden; */
}

.custom-textarea-fixed ::v-deep .v-text-field__details {
  position: relative;
  /* overflow: hidden; */
  word-wrap: break-word;
  white-space: normal;
}

.custom-textarea-fixed ::v-deep .v-textarea .v-text-field__slot {
  /* overflow: hidden; */
}

.custom-textarea-fixed ::v-deep .v-textarea textarea {
  /* overflow: hidden; */
  word-wrap: break-word;
  white-space: pre-wrap;
  resize: none;
}

.additional-textarea-fixed {
  position: relative;
}

.additional-textarea-fixed ::v-deep .v-input__control {
  border-radius: 8px;
  /* overflow: hidden; */
}

.additional-textarea-fixed ::v-deep .v-text-field__details {
  position: relative;
  /* overflow: hidden; */
  word-wrap: break-word;
  white-space: normal;
}

.additional-textarea-fixed ::v-deep .v-textarea textarea {
  /* overflow: hidden; */
  word-wrap: break-word;
  white-space: pre-wrap;
  resize: none;
}

.question-item {
  background: #fafafa;
  padding: 20px;
  border-radius: 12px;
  border-left: 4px solid #1976d2;
}

/* Enhanced Thumb Button Styling */
.thumb-buttons-container {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 16px;
}

.thumb-btn {
  width: 80px !important;
  height: 80px !important;
  border-radius: 50% !important;
  border: 2px solid #e0e0e0 !important;
  background: white !important;
  transition: all 0.3s ease !important;
  position: relative;
}

.thumb-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.thumb-btn.active {
  border-width: 3px !important;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

.thumb-btn.thumb-yes.active {
  border-color: #4caf50 !important;
  background: #f1f8e9 !important;
}

.thumb-btn.thumb-no.active {
  border-color: #f44336 !important;
  background: #ffebee !important;
}

/* Fix expansion panel styling */
.additional-feedback .custom-expansion-panel {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.additional-feedback
  .custom-expansion-panel
  ::v-deep
  .v-expansion-panel-content__wrap {
  padding: 0 16px 16px 16px;
}

.submit-btn {
  min-width: 140px;
  height: 48px !important;
  border-radius: 24px !important;
  text-transform: none !important;
  font-size: 16px !important;
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .review-form-container {
    padding: 10px !important;
  }

  .review-card {
    margin: 0;
    border-radius: 8px !important;
  }

  .question-item {
    padding: 16px;
  }

  .rating-section {
    padding: 10px;
  }

  .thumb-buttons-container {
    gap: 30px;
  }

  .thumb-btn {
    min-width: 60px !important;
    width: 60px !important;
    height: 60px !important;
  }
  .submit-btn {
    /* width: 100%; */
    margin-top: 16px;
  }
  .rating-stars ::v-deep .v-icon {
    padding: 0px;
  }
  .custom-textarea-fixed ::v-deep .v-input__control .v-input__control {
    height: 40px;
  }
}

/* Desktop specific adjustments */
@media (min-width: 960px) {
  .review-form-container {
    padding: 10px !important;
  }

  .question-item {
    padding: 24px;
  }

  .thumb-buttons-container {
    gap: 60px;
  }

  .thumb-btn {
    width: 90px !important;
    height: 90px !important;
  }
}

/* Animation */
.review-card {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading overlay */
.review-card ::v-deep .v-overlay {
  border-radius: 16px;
}

/* Custom scrollbar for mobile */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
::v-deep(
    .v-text-field.v-text-field--outlined > .v-input__control > .v-input__slot
  ) {
  height: 100% !important;
  min-height: 100% !important;
  align-items: center;
}
</style>
